export default function BlogContent() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <article className="prose prose-lg max-w-none">
          <div className="text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 font-medium">
              In 2026, the Vancouver construction market demands agility. Many firms are realizing that a full-time "Detailer" or "Layout Specialist" is a massive fixed cost that doesn't scale. Between BC's rising employer health taxes, WorkSafeBC premiums, and the yearly cost of AutoCAD and Bluebeam licenses, a single employee can cost 30-40% more than their base salary.
            </p>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-calculator-line text-white text-xl"></i>
                </span>
                The True Cost of a Full-Time Employee
              </h2>
              <p className="text-gray-700 mb-4">
                Let's break down what a <strong>full-time layout specialist</strong> actually costs your Vancouver construction firm:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Base Salary:</strong> $65,000 - $85,000/year</li>
                <li><strong>Employer Health Tax (BC):</strong> 1.95% on payroll over $500k</li>
                <li><strong>WorkSafeBC Premiums:</strong> $2.50-$4.00 per $100 of payroll</li>
                <li><strong>CPP & EI Contributions:</strong> ~$5,000/year</li>
                <li><strong>AutoCAD License:</strong> $2,310/year</li>
                <li><strong>Bluebeam Revu License:</strong> $349/year</li>
                <li><strong>High-End Workstation:</strong> $3,000 (amortized over 3 years = $1,000/year)</li>
                <li><strong>Benefits, Vacation, Sick Days:</strong> 15-20% of base salary</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                <strong>Total Annual Cost:</strong> $95,000 - $125,000 for a single employee who may only be productive 60-70% of the time due to project gaps.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-line-chart-line text-white text-xl"></i>
                </span>
                Why Dinari Designs Makes Financial Sense
              </h2>
              <p className="text-gray-700 mb-4">
                When you hire <strong>Dinari Designs</strong>, you convert a fixed monthly expense into a variable project cost. Here's what changes:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>No Payroll Burden:</strong> Zero employer taxes, CPP, EI, or WorkSafeBC premiums</li>
                <li><strong>No Software Costs:</strong> We provide our own AutoCAD and Bluebeam licenses</li>
                <li><strong>No Hardware Investment:</strong> We maintain our own high-performance workstations</li>
                <li><strong>No Downtime Costs:</strong> You only pay when we're actively working on your project</li>
                <li><strong>No Vacation or Sick Pay:</strong> We're available when you need us</li>
                <li><strong>No Training Period:</strong> 20 years of BC construction experience from day one</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                <strong>Result:</strong> You get high-level AutoCAD precision exactly when the project needs it, and your overhead stops the moment the drawings are delivered.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-scales-3-line text-white text-xl"></i>
                </span>
                The Flexibility Factor
              </h2>
              <p className="text-gray-700 mb-0">
                In Vancouver's volatile construction market, project pipelines fluctuate dramatically. A full-time employee represents a <strong>fixed cost</strong> whether you have one project or ten. With <strong>Dinari Designs</strong>, you scale your detailing capacity up or down based on actual demand. When you land three projects simultaneously, we're there. When things slow down, you're not paying for idle time.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </span>
                Risk Mitigation
              </h2>
              <p className="text-gray-700 mb-0">
                What happens if your in-house detailer quits mid-project? You're facing recruitment costs, training time, and potential project delays. With <strong>Dinari Designs</strong>, you eliminate single-point-of-failure risk. We're a stable, established business with redundant capacity and 20 years of proven reliability in the Lower Mainland.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 my-12 text-white">
              <h2 className="text-3xl font-bold mb-4">
                Ready to lean out your overhead?
              </h2>
              <p className="text-lg mb-6 text-teal-50">
                Stop paying for downtime, vacation, and expensive software seats. Get professional <strong>AutoCAD</strong> and <strong>Bluebeam</strong> expertise exactly when you need it—and only when you need it.
              </p>
              <a 
                href="/home#contact" 
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Get a Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-500">Share this article:</span>
                  <div className="flex space-x-3">
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-twitter-x-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-facebook-fill"></i>
                    </a>
                  </div>
                </div>
                <a 
                  href="/knowledge-hub" 
                  className="text-teal-600 hover:text-teal-700 font-medium flex items-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Knowledge Hub
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
